/** FLEET_SYSTEM_SETUP control: register the fleet management system using the SAME fleet dialog
 *  as the tour-operator integration flow (IntegrationCreateDialog → pick "Fleet"), and list the
 *  configured system(s). Mark-done lives on the generic row buttons.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import type { PreflightItem } from '@/api/preflight';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { fleetSystemActions, type TenantCtx } from '../tenantLinks';

import { IntegrationCreateDialog } from '../integrations/IntegrationCreateDialog';

interface FleetSystem {
  id: number;
  provider: string;
  enabled: boolean;
}

function parseSystems(detail: Record<string, unknown>): FleetSystem[] {
  return Array.isArray(detail.fleet_systems)
    ? (detail.fleet_systems as unknown[]).map((s) => {
        const o = s as Record<string, unknown>;
        return { id: Number(o.id), provider: String(o.provider ?? ''), enabled: o.enabled === true };
      })
    : [];
}

export function FleetSystemSetupControl({
  item,
  topId,
  toCode,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const systems = parseSystems(item.detail);
  const ctx: TenantCtx = { id: topId, code: toCode };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {systems.length === 0 ? (
          <Alert severity="info">No fleet management system configured yet.</Alert>
        ) : (
          <Stack spacing={0.5}>
            {systems.map((s) => (
              <Stack key={s.id} direction="row" spacing={1} alignItems="center">
                {s.enabled ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2">
                  <ValueActions actions={fleetSystemActions(s.provider, ctx)}>{s.provider}</ValueActions>
                </Typography>
                <Chip size="small" variant="outlined" label={s.enabled ? 'enabled' : 'disabled'} />
              </Stack>
            ))}
          </Stack>
        )}
        {canManage && (
          <Box>
            <Button size="small" variant="contained" onClick={() => setOpen(true)}>
              {systems.length ? 'Add another fleet system' : 'Set up fleet system'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Opens the integration dialog — choose <strong>Fleet</strong> and enter the provider
              credentials.
            </Typography>
          </Box>
        )}
      </Stack>

      {open && (
        <IntegrationCreateDialog
          open={open}
          toId={topId}
          toCode={toCode}
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </Box>
  );
}
